import { navigateToUrl } from 'single-spa';
import { browserHistory } from 'react-router';
import { delay } from 'redux-saga';
import { call, put, all, takeEvery } from 'redux-saga/effects';
import { change, reset } from 'redux-form';
import store from 'store';
import { getProfile } from 'api/profile';
import * as clevertapRegister from 'clevertap/register';
import * as clevertapLogin from 'clevertap/login';
import { isExternalRedirect } from 'utils/sso';
import { login, AUTH_TYPE } from 'utils/auth';
import Cookies from 'js-cookie';
import i18nInstance from '@moka-bo/i18n';
import { t } from '@lingui/macro';
import * as auth from '../api/auth';
import * as actionNotification from './notifications/actions';
import * as actionMess from './action-mess';
import * as actionRegister from './register/actions';
import * as forgotPasswordAction from './forgotPassword/actions';
import {
  sendingRequest,
  requestError,
  showProgressBar,
  hideProgressBar,
  changeBusinessTypesData,
  changeBusinessCategoriesAllData,
  changeBusinessStartsData,
  changeBusinessSalesData,
  changeFormRegister,
  changeFormResetPassword,
} from './moka/actions';
import { VERIFY_OTP, SET_USER_FORM } from './register/types';
import {
  LOGIN_REQUEST,
  RESEND_VERIFICATION_EMAIL,
  BUSINESS_STARTS_REQUEST,
  BUSINESS_SALES_REQUEST,
  REGISTER_BUSINESS_REQUEST,
  LOAD_RESET_PASSWORD,
  SEND_RESET_PASSWORD,
  LOAD_PREPARE_ENCRYPTION,
  GENERATE_OTP,
} from './constant-mess';

// USAGE: loginFlow
export function* authorize(email, password, phone) {
  // We send an action that tells Redux we're sending a request
  // We then try to register or log in the user, depending on the request
  try {
    const response = yield call(login, email, password, phone);
    return response.data;
  } catch (error) {
    // If we get an error we send Redux the appropiate action and return
    if (error.response) {
      const { data } = error.response;
      if (data.error.user_message) {
        return { error: data.error.user_message, code: data.error.code };
      }
      return { error: error.message, code: data.error.code };
    }
    return { error: error.message };
  }
}

// USAGE: app/auth/components/Login/Login.js
export function* loginFlow(action) {
  const {
    email,
    password,
    phone,
    businessTypesState = [],
    businessCategoriesAllState = [],
  } = action.payload.data;
  const { next } = action.payload;
  yield put(sendingRequest(true));
  yield put(showProgressBar());

  let businessTypes;
  let businessCategories;
  if (
    businessTypesState.length === 0 ||
    businessCategoriesAllState.length === 0
  ) {
    const [
      { data: businessTypesTemp },
      { data: businessCategoriesTemp },
    ] = yield all([
      call(auth.getBusinessTypes),
      call(auth.getBusinessCategoriesAll),
    ]);
    yield put(changeBusinessTypesData(businessTypesTemp));
    yield put(changeBusinessCategoriesAllData(businessCategoriesTemp));
    businessTypes = businessTypesTemp;
    businessCategories = businessCategoriesTemp;
  } else {
    businessTypes = businessTypesState;
    businessCategories = businessCategoriesAllState;
  }

  const authorization = yield call(authorize, email, password, phone);
  const {
    errorTypes,
    user,
    error,
    role,
    access_token: accessToken,
    access_token_exp: accessTokenExpiry,
    refresh_token: refreshToken,
  } = authorization;
  store.set('moka:authType', AUTH_TYPE.MOKA);
  store.set('moka:accessToken', accessToken);
  store.set('moka:accessTokenExpiry', accessTokenExpiry);
  store.set('moka:refreshToken', refreshToken);

  // BO-453 Exclusively for GoFood Middleware UI  const expiryForGofood = new Date(expiry);
  const expiryForGofood = accessTokenExpiry
    ? new Date(accessTokenExpiry)
    : undefined;
  const cookieSettingForGofood = {
    expires: expiryForGofood,
    domain: '.mokapos.com',
  };
  Cookies.set('moka:authType', AUTH_TYPE.MOKA, cookieSettingForGofood);
  Cookies.set('moka:accessToken', accessToken, cookieSettingForGofood);
  Cookies.set('remember_token', accessToken, cookieSettingForGofood);

  yield put(sendingRequest(false));
  yield put(hideProgressBar());
  if (user) {
    // user is logged in
    store.set('response:user_role_name', role.name);
    const profile = yield call(getProfile);
    const { data } = profile;
    try {
      clevertapLogin.setUserProfile({
        profile: data,
        businessTypes,
        businessCategories,
      });
      clevertapLogin.login({ profile: data, role });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    // Persist user info in local storage
    store.set('response:login', data);

    if (next && isExternalRedirect(next)) {
      window.location.href = next;
    } else if (next === 'gostore-merchant') {
      window.location.href = `${process.env.GOSTORE_AUTH_URL}?token=${accessToken}`;
    } else if (next) {
      navigateToUrl(next);
    } else {
      navigateToUrl('/dashboard');
    }
  } else if (error) {
    // user can't loging because have wrong password / email
    yield call(delay, 1000);
    yield put(actionNotification.error(error));
  } else if (errorTypes) {
    // user has logined
    setTimeout(function() {
      window.location.reload();
    }, 1000);
  }
}

// USAGE:
// - app/auth/components/ResendVerificationEmail/ResendVerificationEmail.js
// - app/auth/components/Register/Register.js
export function* resendVerificationEmailFlow(action) {
  const { newEmail, captcha, resetCaptcha } = action.payload.data;
  let { oldEmail } = action.payload.data;
  // UI
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  try {
    // api call
    const response = yield call(auth.resendVerificationEmail, {
      oldEmail,
      newEmail,
      captcha,
    });
    if (response.status === 200) {
      oldEmail = newEmail;
    }
    clevertapRegister.resendVerificationEmail({ email: newEmail });
    yield put(actionNotification.success(response.data.message));
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(actionRegister.resetCaptcha(resetCaptcha + 1));
    yield put(reset('confirmEmailModalForm'));
    yield put(change('confirmEmailModalForm', 'oldEmailAddress', oldEmail));
    yield put(change('confirmEmailModalForm', 'newEmailAddress', newEmail));
    yield put(change('confirmEmailModalForm', 'chapcha', ''));
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

// USAGE: app/auth/components/Register/Register.js
export function* showOtpScreen(action) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { email, password, phone } = action.payload.userForm.user;

  try {
    const response = yield call(auth.validateUser, email, phone);
    const { verifiedEmail, verifiedPhone } = response;
    if (verifiedEmail.present) {
      yield put(
        actionNotification.error(
          i18nInstance._(
            t`This email address is already associated with another account`
          )
        )
      );
      yield put(change('registerForm', 'email', null));
    } else if (verifiedPhone.status === 'Invalid') {
      yield put(
        actionNotification.error(
          i18nInstance._(
            t`This phone number is already associated with another account`
          )
        )
      );
    } else {
      clevertapRegister.clevertapTracker('Registration Sign Up', {
        email,
        phone,
      });
      const { data: generateOtpResponse } = yield call(auth.generateOtp, phone);
      if (generateOtpResponse.status === 'OK') {
        clevertapRegister.clevertapTracker('OTP Receive');
        const { request_id } = generateOtpResponse;
        yield put(actionRegister.setOtpRequestId(request_id));
        yield put(
          changeFormRegister({
            email,
            password,
            confpassword: password,
          })
        );
        yield put(actionRegister.setOtpRegistration(true));
        clevertapRegister.clevertapTracker('OTP Start', { email, phone });
      } else if (generateOtpResponse.status === 'ALREADY_REQUESTED') {
        yield put(
          actionNotification.error(
            i18nInstance._(
              t`Please wait 30 s to sign up with the same phone number`
            )
          )
        );
      }
    }
  } catch (error) {
    yield put(actionNotification.error(i18nInstance._(t`Network Error`)));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

// USAGE: app/auth/components/Register/ProfileSetupForm.js
export function* businessStartsFlow() {
  const currentYear = new Date().getFullYear();
  const yearState = [];
  for (let i = 0; i <= 30; i++) {
    yearState.push({
      id: currentYear - i,
      name: currentYear - i,
    });
  }
  yield put(changeBusinessStartsData(yearState));
}

// USAGE: app/auth/components/Register/ProfileSetupForm.js
export function* businessSalesFlow() {
  const salesState = [
    { id: 5000000, name: i18nInstance._(t`< 5 million IDR`) },
    { id: 10000000, name: i18nInstance._(t`5-10 million IDR`) },
    { id: 20000000, name: i18nInstance._(t`10-20 million IDR`) },
    { id: 100000000, name: i18nInstance._(t`20-100 million IDR`) },
    { id: 999999999, name: i18nInstance._(t`> 100 million IDR`) },
  ];
  yield put(changeBusinessSalesData(salesState));
}

// USAGE: showEmailModalRegister
export function* registerBusinessFlow(action) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { email, password, phone } = action.payload.data.user;
  const response = yield call(auth.validateUser, email, phone);
  const { verifiedEmail, verifiedPhone } = response;
  try {
    if (verifiedEmail.present) {
      yield put(
        actionNotification.error(
          i18nInstance._(
            t`This email address is already associated with another account`
          )
        )
      );
      yield put(change('registerForm', 'email', null));
    } else if (verifiedPhone.status === 'Invalid') {
      yield put(
        actionNotification.error(
          i18nInstance._(
            t`This phone number is already associated with another account`
          )
        )
      );
    } else {
      yield put(actionMess.generateOtpRequest({ phone }));
      const { payload } = action;
      const { data } = payload;
      yield put(
        changeFormRegister({
          email,
          password,
          confpassword: password,
        })
      );
      yield put(actionRegister.setOtpRegistration(true, data));
    }
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

// USAGE: app/auth/components/Register/Register.js
export function* createUser(action) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const {
    code,
    request_id,
    registrationForm,
    businessTypesState,
    businessCategoriesAllState,
    isNativeApps,
  } = action.payload.data;
  const otp = {
    request_id,
    code,
  };
  const userObj = { otp, ...registrationForm };
  yield put(actionRegister.otpError(null));
  try {
    const response = yield call(auth.registerBusiness, userObj);
    if (response.data.status_code) {
      yield put(actionRegister.otpError(response.data.status));
    } else {
      yield put(actionRegister.otpError(null));
      const { password, phone, email } = userObj.user;
      clevertapRegister.clevertapTracker('Registration Created', {
        phone,
        email,
      });
      window.dataLayer.push({
        event: 'Registration Created',
        userPhone: phone,
        userEmail: email,
      });

      if (isNativeApps) {
        window.AndroidInterface.goToVerifyAccount(email);
      } else {
        yield put(
          actionMess.loginRequest(
            { phone, password, businessTypesState, businessCategoriesAllState },
            '/business-info'
          )
        );
      }
    }
  } catch (error) {
    if (error.status_code) {
      yield put(actionRegister.otpError(error.status));
    } else {
      yield put(actionNotification.error('Network Error'));
    }
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

// USAGE:
// - app/auth/components/Register/Register.js
// - registerBusinessFlow
export function* resendOtp(action) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { phone } = action.payload.data;
  try {
    const response = yield call(auth.generateOtp, phone);
    if (response.data.status) {
      const { request_id } = response.data;
      yield put(actionRegister.setOtpRequestId(request_id));
    }
  } catch (error) {
    yield put(actionNotification.error(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

// USAGE: app/auth/components/ResetPassword/ResetPassword.js
export function* loadResetPassword(action) {
  try {
    const response = yield call(auth.getResetPassword, {
      access_token: action.payload.token,
    });
    const user = JSON.parse(response.data.user);
    yield put(
      changeFormResetPassword({
        tokenError: false,
        fullName: `${user.first_name} ${user.last_name}`,
      })
    );
  } catch (error) {
    if (error.status === 404) {
      yield put(
        changeFormResetPassword({
          tokenError: true,
          message: error.data.message,
        })
      );
    }
  }
}

// USAGE: app/auth/components/ResetPassword/ResetPassword.js
export function* sendResetPassword(action) {
  const responsePayload = action.payload.data;
  responsePayload.access_token = action.payload.access_token;
  yield put(sendingRequest(true));
  yield put(showProgressBar(true));
  try {
    yield call(auth.resetPassword, responsePayload);
    yield put(forgotPasswordAction.passwordChangeBy('phone'));
    forwardTo('/success-forgot-password');
  } catch (error) {
    yield put(requestError(error));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

// USAGE: app/auth/components/ResetPassword/ResetPassword.js
export function* loadPrepareEncryption() {
  try {
    const response = yield call(auth.loadPrepareEncryption);
    const responseData = response.data;
    yield put(actionMess.setPrepareEncryption({ responseData }));
  } catch (e) {
    yield put(requestError(e));
  }
}

export default function* root() {
  yield takeEvery(LOGIN_REQUEST, loginFlow);
  yield takeEvery(RESEND_VERIFICATION_EMAIL, resendVerificationEmailFlow);
  yield takeEvery(SET_USER_FORM, showOtpScreen);
  yield takeEvery(BUSINESS_STARTS_REQUEST, businessStartsFlow);
  yield takeEvery(BUSINESS_SALES_REQUEST, businessSalesFlow);
  yield takeEvery(REGISTER_BUSINESS_REQUEST, registerBusinessFlow);
  yield takeEvery(VERIFY_OTP, createUser);
  yield takeEvery(GENERATE_OTP, resendOtp);
  yield takeEvery(LOAD_RESET_PASSWORD, loadResetPassword);
  yield takeEvery(SEND_RESET_PASSWORD, sendResetPassword);
  yield takeEvery(LOAD_PREPARE_ENCRYPTION, loadPrepareEncryption);
}

// Little helper function to abstract going to different pages
function forwardTo(location) {
  browserHistory.push(location);
}
